// React
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

// Appt
import Box from '../../simple/box/Box.js'
import Text from '../../simple/text/Text.js'
import TextArea from '../../simple/input/TextArea.js'

// Font Awesome
import styled from 'styled-components'
import Grid from '../../simple/grid/Grid.js'
// import Divider from '../../simple/divider/Divider.js'
import Button from '../../simple/button/Button.js'
import FontAwesome from '../fontAwesome/FontAwesome.js'
import LocalStorageService from '../../../services/LocalStorageService'
import { getRecordTerm } from '../../../services/TermsService'

/**
 * `MultiSelectCard` renders a Card to display a counter of items eg. number of posts
 */


const StyledWrapper = styled(Box)`
`

const StyledCard = styled(Box)`
  border: 2px solid #aaa;
  border-radius: 4px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  cursor: pointer;
  position: relative;

`

const StyledAddCard = styled(StyledCard)`
  align-items: center;
  justify-content: center;
`

const StyledCustomCard = styled(StyledCard)`
  border-style: dashed;
`

const StyledCustomCheckbox = styled(Box)`
`

const StyledCustomTextArea = styled(TextArea)`
  min-height: 80px;
  font-weight: bold;

  &::placeholder {
    color: ${({ theme }) => theme?.global?.colors?.secondary || '#163567'};
    opacity: 1;
    font-weight: normal;
  }
`

const StyledToggleIcon = styled(Box)`
  cursor: pointer;
`


const toKey = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '')

const normalizeValues = (values = []) => {
  const normalized = []
  const seen = new Set()

  ;(Array.isArray(values) ? values : []).forEach((value) => {
    const trimmed = typeof value === 'string' ? value.trim() : ''
    const key = toKey(trimmed)

    if (!trimmed || seen.has(key)) {
      return
    }

    seen.add(key)
    normalized.push(trimmed)
  })

  // return unique values
  return [...new Set(normalized) ]
}

const includesValue = (values = [], target = '') => {
  const targetKey = toKey(target)

  return (values || []).some(value => String(toKey(value)) === String(targetKey))
}

const removeValue = (values = [], target = '') => {
  const targetKey = toKey(target)

  return (values || []).filter(value => String(toKey(value)) !== String(targetKey))
}

const createCustomCard = (value = '', isSelected = true) => ({
  id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  value,
  isSelected
})

const shouldIgnoreCustomCardToggle = (event) => {
  if (!event?.target?.closest) {
    return false
  }

  return Boolean(event.target.closest('textarea, input, button, label'))
}

const SelectionToggleIcon = ({ checked, onToggle }) => (
  <StyledToggleIcon
    margin={{ right: 'small' }}
    onClick={(event) => {
      event.stopPropagation()
      onToggle()
    }}
  >
    <Text color={checked ? 'white' : 'dark-4'}>
      <FontAwesome color={checked ? 'secondary' : 'light-6'} icon={checked ? ['fas', 'check-square'] : ['fas', 'square']} />
    </Text>
  </StyledToggleIcon>
)

export const MultiSelectCard = (props) => {

  const [options, setOptions] = useState([])
  const [selectedOptions, setSelectedOptions] = useState([])
  const [customOptions, setCustomOptions] = useState([])

  useEffect(() => {
    let effectiveSelected = props.selected || []
    if (!effectiveSelected?.length && props.defaultSelected) {
      effectiveSelected = props.defaultSelected
    }

    setSelectedOptions(normalizeValues(effectiveSelected))

  }, [props.defaultSelected, props.selected])


  useEffect(() => {
    const uniqueOptions = normalizeValues(props.options)
    uniqueOptions.sort()
    setOptions(uniqueOptions)
  }, [props.options])

  useEffect(() => {
    setSelectedOptions(normalizeValues(props.selected))
     emitSelection(props.selected)
  }, [props.selected])


  useEffect(() => {
    const customSelectedValues = normalizeValues((Array.isArray(props.selected) ? props.selected : []).filter(option => option && !includesValue(options, option)))

    setCustomOptions(previousCustomOptions => {
      const existingValues = previousCustomOptions.map(option => option.value.trim()).filter(Boolean)
      const missingCustomValues = customSelectedValues.filter(option => !includesValue(existingValues, option))
      const nextCustomOptions = [
        ...previousCustomOptions,
        ...missingCustomValues.map(value => createCustomCard(value, true))
      ]

      return nextCustomOptions.map(customCard => ({
        ...customCard,
        isSelected: customCard.value.trim()
          ? includesValue(customSelectedValues, customCard.value.trim())
          : customCard.isSelected
      }))
    })
  }, [options, props.selected])

  const emitSelection = (nextSelected) => {
    const uniqueSelected = normalizeValues(nextSelected)

    setSelectedOptions(uniqueSelected)

    if (props.onChange) {
      props.onChange(uniqueSelected)
    }
  }

  const handleOptionToggle = (option) => {
    const nextSelected = includesValue(selectedOptions, option)
      ? removeValue(selectedOptions, option)
      : [...selectedOptions, option]

    emitSelection(nextSelected)
  }

  const handleCustomChange = (customCardId, event) => {
    const nextValue = event.target.value
    const nextCustomValue = nextValue.trim()
    const currentCustomCard = customOptions.find(customCard => customCard.id === customCardId)
    const previousCustomValue = currentCustomCard?.value?.trim() || ''
    const wasSelected = Boolean(currentCustomCard?.isSelected)

    setCustomOptions(previousCustomOptions => previousCustomOptions.map(customCard => {
      if (customCard.id !== customCardId) {
        return customCard
      }

      return {
        ...customCard,
        value: nextValue,
        isSelected: customCard.isSelected
      }
    }))

    if (wasSelected && previousCustomValue !== nextCustomValue) {
      const nextSelected = removeValue(selectedOptions, previousCustomValue)

      if (nextCustomValue) {
        nextSelected.push(nextCustomValue)
      }

      emitSelection(nextSelected)
    }
  }

  const handleAddCustomOption = () => {
    setCustomOptions(previousCustomOptions => ([
      ...previousCustomOptions,
      createCustomCard()
    ]))
  }

  const renderCustomOption = () => {

    const handleCustomOptionToggle = (customCard) => {
      const nextChecked = !customCard.isSelected
      const customCardValue = customCard.value.trim()

      setCustomOptions(previousCustomOptions => previousCustomOptions.map(item => {
        if (item.id !== customCard.id) {
          return item
        }

        return {
          ...item,
          isSelected: nextChecked
        }
      }))

      if (nextChecked && customCardValue) {
        emitSelection([...selectedOptions, customCardValue])
      } else {
        emitSelection(removeValue(selectedOptions, customCardValue))
      }
    }

    return (
      <>
        {customOptions
          .filter(customCard => {
            const value = customCard.value.trim()

            if (!value) {
              return true
            }

            return !includesValue(options, value)
          })
          .map(customCard => {
          const isSelected = customCard.isSelected

          return (
            <StyledCustomCard
              round='small'
              key={customCard.id}
              padding='small'
              background={isSelected ? 'primary' : 'light-2'}
              direction='row'
              onClick={(event) => {
                if (shouldIgnoreCustomCardToggle(event)) {
                  return
                }

                handleCustomOptionToggle(customCard)
              }}
            >
              <StyledCustomCheckbox>
                <SelectionToggleIcon
                  checked={isSelected}
                  onToggle={() => handleCustomOptionToggle(customCard)}
                />
              </StyledCustomCheckbox>
              <Box>

                <StyledCustomTextArea
                  plain
                  resize='vertical'
                  placeholder='Add a custom option'
                  value={customCard.value}
                  autoFocus={customCard.value === ''}
                  border={false}
                  focusIndicator={false}
                  style={{ color: 'secondary' }}
                  weight='bold'
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                  onKeyUp={(event) => event.stopPropagation()}
                  onChange={(event) => handleCustomChange(customCard.id, event)}
                />
              </Box>
            </StyledCustomCard>
          )
        })}
        <StyledAddCard
          background='light-2'
          onClick={handleAddCustomOption}
        >
          <Text color='grey'>+ Add</Text>
        </StyledAddCard>
      </>
    )
  }

  return (
    <StyledWrapper
      border={{ color: 'light-4', size: 'small' }}
      margin={{ top: 'small', bottom: 'small' }}
      pad='small'
      round='small'
      className='multi-select-card'
    >

      <Box direction='row' gap='small' justify='between' align='center'>
        {props.title && <Text size='medium' weight='bold'>{props.title}</Text>}
        <Button
          label={<Text><FontAwesome color='brand' icon={['fas', 'question-circle']} /></Text>}
          plain
          tip={{
            content: (
              <Box background={{ color: 'white' }} border={{ color: 'brand', size: 'small' }} direction='column' pad='small'>
                <Text>Select the options you&apos;d like to include in the {getRecordTerm(LocalStorageService.get('terms'), 'smartPlanBuilderTerm', 'Smart Plan')}</Text>
              </Box>)
          }}
        />
      </Box>

      {props.description && <Text>{props.description}</Text>}
      <Grid
        columns={props.columns || ['32%', '32%', '32%']}
        gap='small'
        margin={{ top: 'small' }}
      >
        {options.map(option => (
          <StyledCard
            round='small'
            key={option}
            padding='small'
            background={includesValue(selectedOptions, option) ? 'primary' : 'light-1'}
            border={includesValue(selectedOptions, option) ? { color: 'primary', size: 'small' } : { color: 'secondary', size: 'small' }}
            onClick={() => handleOptionToggle(option)}
          >
            <SelectionToggleIcon
              checked={includesValue(selectedOptions, option)}
              onToggle={() => handleOptionToggle(option)}
            />
            <Text
              color={includesValue(selectedOptions, option) ? 'secondary' : 'secondary'}
              weight='bold'
            >{option}</Text>
          </StyledCard>
        ))}
        {renderCustomOption()}
      </Grid>

    </StyledWrapper>
  )
}


MultiSelectCard.propTypes = {
  /**
   * The title for the card
   */
  title: PropTypes.string,
  /**
    * The selected values for the card.
   */
  selected: PropTypes.array,
  /**
  * Called when the selected values change.
  */
  onChange: PropTypes.func,

  /**
   * The options to display in the card.
   */
  options: PropTypes.array,
  /**
   * The default selected values for the card.
   */
  defaultSelected: PropTypes.array,

  description: PropTypes.string,
  /**
   * A large sized number displayed in the card
   */
  onClick: PropTypes.func,
  /**
   * The icon displayed on the card
   */
}

SelectionToggleIcon.propTypes = {
  checked: PropTypes.bool,
  onToggle: PropTypes.func
}


export default MultiSelectCard
